import { getApiBaseUrl } from "../../utils/ssoProvider";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { IoWarningOutline } from "react-icons/io5";
import PopupModal from "../PopupModal";
import axiosInstance from "../../services/axiosInstance";
import { closeTemplateConflictModal } from "../../redux/templateConflictModalSlice";
import { fetchTemplateConflicts } from "../../redux/templateConflictsSlice";

const API_URL = getApiBaseUrl();

// Maps this modal's UI choice to the backend's ConflictResolutionMethod enum value.
const RESOLUTION_METHODS = {
  refine: "refine_description",
  merge: "merge_template",
  delete: "delete_template",
};

const RESOLUTION_OPTIONS = [
  {
    value: "refine",
    label: "Refine this description",
    description: "Narrow the wording so each template covers a distinct situation.",
    confirmLabel: "Update description",
  },
  {
    value: "merge",
    label: "Merge into the existing template",
    description: "Keep one template and combine both messages.",
    confirmLabel: "Merge templates",
  },
  {
    value: "delete",
    label: null, // resolved dynamically below (includes the existing template's name)
    description: "Remove the older template and keep this one.",
    confirmLabel: "Delete template",
  },
];

// Renders on every authenticated screen (mounted from layouts/ContentLayout.jsx) so a
// conflict detected sometime after a create/update stays visible even if the user has
// already navigated away while the backend's background scan was still running (see
// redux/templateConflictsSlice.js's watchForTemplateConflict).
const GlobalTemplateConflictModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isOpen, conflictId, overlapPercent, templateA, templateB } = useSelector(
    state => state.templateConflictModal
  );
  const [resolution, setResolution] = useState("refine");
  const [content, setContent] = useState("");
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  // Which side is shown as "Existing" (and therefore kept/deleted-target) vs "current"
  // (refined in place, or deleted on merge) - swappable via the dropdown below so either
  // template in the pair can be the one that's kept.
  const [existingId, setExistingId] = useState(null);
  const [lastConflictId, setLastConflictId] = useState(null);

  // Reset which side is shown as "Existing" whenever a new conflict opens (React's documented
  // pattern for resetting state on prop change during render - avoids an extra effect-driven
  // render/re-fetch flicker). Defaults to templateB, matching this modal's original pairing.
  if (isOpen && conflictId !== lastConflictId) {
    setLastConflictId(conflictId);
    setExistingId(templateB?.id ?? null);
  }

  const existingTemplate = existingId === templateA?.id ? templateA : templateB;
  const currentTemplate = existingId === templateA?.id ? templateB : templateA;
  const currentTemplateId = currentTemplate?.id ?? null;

  // Draft the content for the selected approach up front, so the user is reviewing/editing
  // an AI-written starting point rather than writing it from scratch. Not needed for delete.
  // Also re-drafts automatically if the user swaps which side is "current" via the dropdown.
  useEffect(() => {
    if (!isOpen || !conflictId || !currentTemplateId || resolution === "delete") {
      // Clear a stale "true" left over from a fetch this effect run is superseding/skipping
      // (e.g. the user switched to "delete", or swapped sides, before the previous one resolved) -
      // otherwise the confirm button would stay disabled forever.
      setIsSuggesting(false);
      return;
    }
    let cancelled = false;
    setIsSuggesting(true);
    axiosInstance
      .post(`${API_URL}/v1/store/draft/template-conflicts/${conflictId}/suggest`, {
        resolution_method: RESOLUTION_METHODS[resolution],
        current_template_id: currentTemplateId,
      })
      .then(response => {
        if (!cancelled) setContent(response.data?.suggested_content || "");
      })
      .catch(error => {
        console.error("Failed to draft a resolution suggestion:", error);
      })
      .finally(() => {
        if (!cancelled) setIsSuggesting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, conflictId, currentTemplateId, resolution]);

  if (!isOpen || !templateA || !templateB) {
    return null;
  }

  const selectedOption = RESOLUTION_OPTIONS.find(option => option.value === resolution);
  const deleteLabel = `Delete "${existingTemplate.name}"`;

  const handleClose = () => {
    dispatch(closeTemplateConflictModal());
    setResolution("refine");
    setContent("");
  };

  const handleConfirmResolution = async () => {
    if (resolution !== "delete" && !content.trim()) {
      toast.error("Add some content before continuing.");
      return;
    }
    setIsResolving(true);
    try {
      await axiosInstance.patch(`${API_URL}/v1/store/draft/template-conflicts/${conflictId}/resolve`, {
        resolution_method: RESOLUTION_METHODS[resolution],
        current_template_id: currentTemplateId,
        content: resolution === "delete" ? undefined : content,
      });
      if (resolution === "refine") {
        toast.success("Description updated.");
      } else if (resolution === "merge") {
        toast.success(`Merged into "${existingTemplate.name}".`);
      } else {
        toast.success(`"${existingTemplate.name}" deleted.`);
      }
      dispatch(fetchTemplateConflicts()); // refresh the banner/badge now that this one's resolved
      handleClose();
      navigate("/templates-page");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resolve the conflict.");
    }
    setIsResolving(false);
  };

  return (
    <PopupModal closeModal={handleClose} className="bg-white w-[560px] max-w-[92vw] max-h-[90vh] overflow-y-auto">
      <div className="flex items-start gap-3 mb-3">
        <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center">
          <IoWarningOutline className="w-5 h-5 text-[#D97706]" />
        </span>
        <div>
          <h3 className="text-[16px] font-bold text-[#101828] font-dmsans">Conflicting template detected</h3>
          <p className="text-[13px] text-gray-500 mt-1">
            This template overlaps with an existing one. When both match, the AI can't tell which reply to use.
          </p>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-3 mb-3 flex items-start justify-between gap-3 bg-[#F9FAFB]">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <span className="flex-shrink-0 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F0F0F2] text-gray-600">
            Existing
          </span>
          <div className="min-w-0 flex-1">
            <select
              value={existingTemplate.id}
              onChange={e => setExistingId(Number(e.target.value))}
              data-testid="existing-template-select"
              className="text-[13px] font-semibold text-[#101828] bg-white border border-gray-200 rounded-md px-1.5 py-0.5 focus:outline-none focus:ring-0 w-full max-w-full truncate cursor-pointer"
            >
              <option value={templateA.id}>{templateA.name}</option>
              <option value={templateB.id}>{templateB.name}</option>
            </select>
            <p className="text-[12px] text-gray-500 mt-0.5">{existingTemplate.description}</p>
          </div>
        </div>
        <span className="flex-shrink-0 text-[12px] font-semibold text-red-600 whitespace-nowrap">
          {overlapPercent}% overlap
        </span>
      </div>

      <p className="text-[13px] font-medium text-[#101828] mb-2">How do you want to resolve it?</p>

      <div className="flex flex-col gap-2 mb-3">
        {RESOLUTION_OPTIONS.map(option => {
          const isSelected = resolution === option.value;
          const label = option.value === "delete" ? deleteLabel : option.label;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setResolution(option.value)}
              className={`text-left border rounded-lg p-2 flex items-start gap-3 ${
                isSelected ? "border-[#23BE54] bg-[#23BE5410]" : "border-gray-200 bg-white"
              }`}
            >
              <span
                className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center ${
                  isSelected ? "border-[#23BE54]" : "border-gray-300"
                }`}
              >
                {isSelected && <span className="w-2 h-2 rounded-full bg-[#23BE54]"></span>}
              </span>
              <div>
                <p className="text-[13px] font-semibold text-[#101828]">{label}</p>
                <p className="text-[12px] text-gray-500 mt-0.5">{option.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {resolution !== "delete" && (
        <>
          <label className="text-[13px] font-medium text-[#101828] flex items-center gap-2 mb-1">
            {resolution === "refine"
              ? "When should the AI use THIS template?"
              : "Merged reply (edit before confirming)"}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#23BE5426] text-[#23BE54]">
              Used by AI
            </span>
          </label>
          <div className="relative mb-3">
            <textarea
              rows={resolution === "refine" ? 3 : 6}
              placeholder={
                resolution === "refine" ? "e.g. Only when the order has already shipped" : "The merged reply body"
              }
              value={content}
              disabled={isSuggesting}
              onChange={e => setContent(e.target.value)}
              className="w-full text-[13px] py-2 px-3 border border-gray-200 bg-[#F9FAFB] rounded-md focus:outline-none resize-none disabled:opacity-60"
            />
            {isSuggesting && (
              <div className="absolute inset-0 flex items-center justify-center gap-2 text-[13px] text-gray-500 bg-[#F9FAFB] rounded-md">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#2B2E3A]" />
                <span>Drafting a suggestion...</span>
              </div>
            )}
          </div>
        </>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 rounded-md border border-gray-300 bg-white text-[13px] font-medium text-gray-700"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirmResolution}
          disabled={isSuggesting || isResolving}
          className="px-4 py-2 rounded-md bg-[#2B2E3A] text-[13px] font-medium text-[#96F3C3] disabled:opacity-60"
        >
          {isResolving ? "Resolving..." : selectedOption?.confirmLabel}
        </button>
      </div>
    </PopupModal>
  );
};

export default GlobalTemplateConflictModal;
